import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["fill", "outlineColor"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { mapStyle } from '@gisatcz/ptr-utils';
import { find as _find, forEach as _forEach, forIn as _forIn, isEmpty as _isEmpty } from 'lodash';
import memoize from 'moize';
import chroma from 'chroma-js';
import view from '../../utils/view';
import defaultStyles from '../../constants/styles';
/**
 * Get style object ready for usage in DeckGl-based layers, where colors are represented by RGB array
 * @param styleObject {Object} Panther style object
 * @returns {Object} DeckGl-ready style object
 */

function getDeckReadyStyleObject(styleObject) {
  var fill = styleObject.fill,
      outlineColor = styleObject.outlineColor,
      restProps = _objectWithoutProperties(styleObject, _excluded);

  var deckReadyStyleObject = _objectSpread({}, restProps);

  if (fill) {
    deckReadyStyleObject.fill = getDeckReadyColor(fill);
  }

  if (outlineColor) {
    deckReadyStyleObject.outlineColor = getDeckReadyColor(outlineColor);
  }

  return deckReadyStyleObject;
}
/**
 * Get array representing RGB values
 * @param hexColor {string} color hex code
 * @returns {Array} RGB array
 */


function getDeckReadyColor(hexColor) {
  return chroma(hexColor).rgb();
}
/**
 * Style definition prepared for the usage in DeckGl-based vector layers to optimize rendering performance
 * @param style {Object} Panther Style.definition
 * @returns {baseStyle: Object, attributeStyles: Array}
 */


function getStylesDefinitionForDeck(style) {
  // TODO multiple rules and filters?
  var styles = (style === null || style === void 0 ? void 0 : style.rules[0].styles) || [defaultStyles.vectorFeatureStyle["default"]];

  if (styles) {
    var baseStyle = getDeckReadyStyleObject(styles[0]);
    var styleForDeck = {
      baseStyle: baseStyle
    };
    var attributeStyles = [];

    for (var i = 1; i < styles.length; i++) {
      var _style = styles[i];
      var attributeKey = _style.attributeKey;

      if (_style.attributeValues) {
        (function () {
          var values = {};

          _forIn(_style.attributeValues, function (style, value) {
            values[value] = getDeckReadyStyleObject(style);
          });

          attributeStyles.push({
            attributeKey: attributeKey,
            attributeValues: values
          });
        })();
      } else if (_style.attributeClasses) {
        (function () {
          var classes = [];

          _forEach(_style.attributeClasses, function (style) {
            classes.push(getDeckReadyStyleObject(style));
          });

          attributeStyles.push({
            attributeKey: attributeKey,
            attributeClasses: classes
          });
        })();
      }
    }

    if (!_isEmpty(attributeStyles)) {
      styleForDeck.attributeStyles = attributeStyles;
    }

    return styleForDeck;
  } else {
    return null;
  }
}
/**
 * Return renderAs rules according to given box range
 * @param renderAs {Array} List of renderAs rule sets
 * @param boxRange {number} Panther's mapView.boxRange
 * @returns {Object} renderAs rules
 */


function getRenderAsRulesByBoxRange(renderAs, boxRange) {
  if (renderAs && boxRange) {
    return _find(renderAs, function (renderAsItem) {
      return view.isBoxRangeInRange(boxRange, renderAsItem.boxRangeRange);
    });
  } else {
    return null;
  }
}
/**
 * Return array of RGBA values, where A is from 0 to 255
 * @param rgbColorArray {Array} Array of RGB values
 * @param opacity {number} From 0 to 1
 * @returns {Array}
 */


function getRgbaColorArray(rgbColorArray, opacity) {
  if (opacity || opacity === 0) {
    return [].concat(_toConsumableArray(rgbColorArray), [Math.floor(opacity * 255)]);
  } else {
    return rgbColorArray;
  }
}
/**
 * Get style for given feature
 * @param style {Object} Style definition suitable for Deck
 * @param feature {GeoJSONFeature}
 * @returns {Object} style object
 */


function getStyleForFeature(style, feature) {
  var _style$attributeStyle;

  if ((_style$attributeStyle = style.attributeStyles) !== null && _style$attributeStyle !== void 0 && _style$attributeStyle.length) {
    var finalStyle = _objectSpread({}, style.baseStyle);

    style.attributeStyles.forEach(function (attributeStyle) {
      finalStyle = _objectSpread(_objectSpread({}, finalStyle), getStyleObjectForAttribute(attributeStyle, feature.properties));
    });
    return finalStyle;
  } else {
    return style.baseStyle;
  }
}
/**
 * @param attributeStyleDefinition {Object} Style definition for given attribute key
 * @param attributes {Object} Feature attributes
 * @return {Object} Style object for given attribute key
 */


function getStyleObjectForAttribute(attributeStyleDefinition, attributes) {
  if (Object.hasOwn(attributes, attributeStyleDefinition.attributeKey)) {
    var value = attributes[attributeStyleDefinition.attributeKey];

    if (value === null || value === undefined) {
      return {};
    } else {
      if (attributeStyleDefinition.attributeClasses) {
        return mapStyle.getStyleObjectForIntervals(attributeStyleDefinition.attributeClasses, value);
      } else if (attributeStyleDefinition.attributeValues) {
        return mapStyle.getStyleObjectForValues(attributeStyleDefinition.attributeValues, value);
      } // TODO add other cases
      else {
        return {};
      }
    }
  } else {
    return {};
  }
}

export default {
  getRgbaColorArray: getRgbaColorArray,
  getDeckReadyColor: getDeckReadyColor,
  getDeckReadyStyleObject: getDeckReadyStyleObject,
  getStylesDefinitionForDeck: memoize(getStylesDefinitionForDeck, {
    maxSize: 100
  }),
  getStyleForFeature: getStyleForFeature,
  getStyleObjectForAttribute: getStyleObjectForAttribute,
  getRenderAsRulesByBoxRange: getRenderAsRulesByBoxRange
};